import {
    Component,
    OnInit,
    OnDestroy,
    Output,
    EventEmitter,
    ViewChild,
    Type as AngularCoreType,
    ComponentFactoryResolver,
    AfterViewInit,
    ElementRef,
    Renderer2,
    Input
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { FormField } from '../../../models/form-builder/form-field.model';
import { FormInstanceElement } from '../../../models/form-builder/form-instance-element.model';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { FormFieldTypeEnum, ValueConcatenatorFieldType } from '../../../models/form-builder/form-field-types';
import { DynamicComponentHostDirective } from '../../../directives/dynamic-content-host.directive';
import { TooltipPosition } from '@angular/material/tooltip';
import { AlertDialogComponent, AlertDialogModel } from '../../../../shared/dialogs/alert/alert-dialog.component';

// Note:  import the form field classes.
import { ShortTextFormFieldComponent } from '../short-text-form-field/short-text-form-field.component'
import { CheckboxFormFieldComponent } from '../checkbox-form-field/checkbox-form-field.component';
import { RadioButtonsFormFieldComponent } from '../radio-buttons-form-field/radio-buttons-form-field.component';
import { DateFormFieldComponent } from '../date-form-field/date-form-field.component';
import { IntegerFormFieldComponent } from '../integer-form-field/integer-form-field.component';
import { CurrencyFormFieldComponent } from '../currency-form-field/currency-form-field.component';
import { LabelFormFieldComponent } from '../label-form-field/label-form-field.component';
import { DropDownFormFieldComponent } from '../drop-down-form-field/drop-down-form-field.component';
import { GridFormFieldComponent } from '../grid-form-field/grid-form-field.component';
import { CascadingDropDownFormFieldComponent } from '../cascading-drop-down-form-field/cascading-drop-down-form-field.component';
import { AttachmentFormFieldComponent } from '../attachment-form-field/attachment-form-field.component';
import { FootnoteFormFieldComponent } from '../footnote-form-field/footnote-form-field.component';
import { FormulaFormFieldComponent } from '../formula-form-field/formula-form-field.component';
import { InstructionsFormFieldComponent } from '../instructions-form-field/instructions-form-field.component';
import { RawHtmlFormFieldComponent } from '../raw-html-form-field/raw-html-form-field.component';
import { RichTextFormFieldComponent } from '../rich-text-form-field/rich-text-form-field.component';
import { DevOnlyFormFieldComponent } from '../dev-only-form-field/dev-only-form-field.component';
import { MultiCheckboxFormFieldComponent } from '../multi-checkbox-form-field/multi-checkbox-form-field.component';
import { MultiDropDownFormFieldComponent } from '../multi-drop-down-form-field/multi-drop-down-form-field.component';
import { ShowToHideFromComponent } from '../show-to-hide-from/show-to-hide-from.component';
import { GridCascadingDropDownFormFieldComponent } from '../grid-cascading-dropdown/grid-cascading-dropdown.component';
import { IFieldDefinitionLogic } from '../../../interfaces/ifield-definition-logic.interface';
import { ContactsFormFieldComponent } from '../contacts-form-field/contacts-form-field.component';
import { CommentsFormFieldComponent } from '../comments-form-field/comments-form-field.component';
import { MarkDataReadOnlyComponent } from '../mark-data-readonly/mark-data-readonly.component';
import { EditorNameFormFieldComponent } from '../editor-name-form-field/editor-name-form-field.component';
import { LastEditedDateFormFieldComponent } from '../last-edited-date-form-field/last-edited-date-form-field.component';


// Import the enum of form field modes.
//import { FormFieldMode } from '../form-field-mode.enum';

//import { IFieldNameToComponent } from '../form-field-base/form-field-base.component';
import { IFieldNameToFormField } from '../../../interfaces/iform-field-component';
import { FormFieldProcessingPhaseEnum } from '../../../enums/form-field-processing-phase.enum';
import { IGridRow } from '../../../interfaces/grid-row.interface';
import { FormFieldOnInitPropertyEnum } from '../../../enums/form-field-on-init-property.enum';
//import { FormInstanceElementRequest } from '../../../models/form-instance-element-request.model';
import { FormFieldConfigurationSaveReason } from '../../../enums/form-field-save-config-reason.enum';

import {
    ShortTextFieldType,
    DateFieldType,
    CheckboxFieldType,
    RadioButtonsFieldType,
    IntegerFieldType,
    CurrencyFieldType,
    LabelFieldType,
    DropDownFieldType,
    GridFieldType,
    CascadingDropDownFieldType,
    GridCascadingDropDownFieldType,
    AttachmentFieldType,
    FormulaFieldType,
    InstructionsFieldType,
    RawHTMLFieldType,
    RichTextFieldType,
    DevOnlyFieldType,
    FootnoteFieldType,
    MultiCheckboxFieldType,
    MultiDropDownFieldType,
    ShowToFieldType,
    HideFromFieldType,
    ContactsFieldType,
    CommentsFieldType,
    HTMLLinkFieldType,
    MarkDataReadOnlyFieldType,
    EditorNameFieldType,
    LastEditedDateFieldType
} from '../../../models/form-builder/form-field-types';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FormFieldTypeAndNameService } from '../../../services/form-field-type-and-name.service';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { HtmlLinkFieldComponent } from '../html-link-field/html-link-field.component';
import { TargetFieldConditionalLogicInstructions } from '../../../models/site-metadata/target-field-logic-instructions';
import { ValueConcatenatorFormFieldComponent } from '../value-concatenator-form-field/value-concatenator-form-field.component';

// Define a class for defining a dynamic component.
class DynamicComponentInitInfo {
    public fieldType: string = null;
    public componentClass: AngularCoreType<any>;
}

class FormFieldTypeSelection {
    public fieldDefinitionClassName: string;
    public iconType: string;
    public iconName: string;
    public fieldName: string;
}

// Define an enum for dictating the operating mode, either static/original/switch
// case mode or dynamic component mode, the latter still being tested.
enum WrapperOperatingMode {
    STATIC_COMPONENT = "Static Component",
    DYNAMIC_COMPONENT = "Dynamic Component"
}

// Define a map/dictionary of form field type names to form field classes, used only
// within this class/code file for indexed/quick lookups of form field classes by name.

// Note:  try to replace the following data/logic with a call to a method within
//        FormFieldTypeAndNameService.
const mapFormFieldTypesToClasses = {};
mapFormFieldTypesToClasses[ShortTextFieldType] = ShortTextFormFieldComponent;
mapFormFieldTypesToClasses[CheckboxFieldType] = CheckboxFormFieldComponent;
mapFormFieldTypesToClasses[RadioButtonsFieldType] = RadioButtonsFormFieldComponent;
mapFormFieldTypesToClasses[DateFieldType] = DateFormFieldComponent;
mapFormFieldTypesToClasses[IntegerFieldType] = IntegerFormFieldComponent;
mapFormFieldTypesToClasses[CurrencyFieldType] = CurrencyFormFieldComponent;
mapFormFieldTypesToClasses[LabelFieldType] = LabelFormFieldComponent;
mapFormFieldTypesToClasses[DropDownFieldType] = DropDownFormFieldComponent;
mapFormFieldTypesToClasses[GridFieldType] = GridFormFieldComponent;
mapFormFieldTypesToClasses[CascadingDropDownFieldType] = CascadingDropDownFormFieldComponent;
mapFormFieldTypesToClasses[GridCascadingDropDownFieldType] = GridCascadingDropDownFormFieldComponent;
mapFormFieldTypesToClasses[AttachmentFieldType] = AttachmentFormFieldComponent;
mapFormFieldTypesToClasses[InstructionsFieldType] = InstructionsFormFieldComponent;
mapFormFieldTypesToClasses[RawHTMLFieldType] = RawHtmlFormFieldComponent;
mapFormFieldTypesToClasses[FormulaFieldType] = FormulaFormFieldComponent;
mapFormFieldTypesToClasses[ValueConcatenatorFieldType] = ValueConcatenatorFormFieldComponent;
mapFormFieldTypesToClasses[RichTextFieldType] = RichTextFormFieldComponent;
mapFormFieldTypesToClasses[DevOnlyFieldType] = DevOnlyFormFieldComponent;
mapFormFieldTypesToClasses[FootnoteFieldType] = FootnoteFormFieldComponent;
mapFormFieldTypesToClasses[MultiCheckboxFieldType] = MultiCheckboxFormFieldComponent;
mapFormFieldTypesToClasses[MultiDropDownFieldType] = MultiDropDownFormFieldComponent;
mapFormFieldTypesToClasses[ShowToFieldType] = ShowToHideFromComponent;
mapFormFieldTypesToClasses[HideFromFieldType] = ShowToHideFromComponent;
mapFormFieldTypesToClasses[ContactsFieldType] = ContactsFormFieldComponent;
mapFormFieldTypesToClasses[CommentsFieldType] = CommentsFormFieldComponent;
mapFormFieldTypesToClasses[HTMLLinkFieldType] = HtmlLinkFieldComponent; //TEAMS-835: KLW - Needed for the new HTML Link field
mapFormFieldTypesToClasses[MarkDataReadOnlyFieldType] = MarkDataReadOnlyComponent;
mapFormFieldTypesToClasses[EditorNameFieldType] = EditorNameFormFieldComponent;
mapFormFieldTypesToClasses[LastEditedDateFieldType] = LastEditedDateFormFieldComponent;

//declare var $: any; // Declares jQuery

// Note:  please note the 'providers' definition below, as it is needed.
//        Without it, you will get the following exception:
//
//             No value accessor for form control with unspecified name
//
// The above exception gets thrown when a component, in this case our
// base class, implements interface 'ControlValueAccessor' and does not
// provide the 'providers' definition below.  Implementing the
// 'ControlValueAccessor' interface allows a form field component to
// support [(ngMode)], so users of the component can use [(ngModel)].
@Component({
    selector: 'app-form-field-wrapper',
    templateUrl: './form-field-wrapper.component.html',
    styleUrls: ['./form-field-wrapper.component.scss', '../form-fields.scss'],
    standalone: false, //Kevin
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FormFieldWrapperComponent,
            multi: true
        }
    ]
})
export class FormFieldWrapperComponent extends FormFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    // Properties.

    // Note:  inputs have been moved into my base class.
    @Output() onInit = new EventEmitter();
    @Output() onEdit = new EventEmitter();
    @Output() onConditionalLogicEdit = new EventEmitter();
    @Output() onContextMenuClick = new EventEmitter();
    @Output() designChange = new EventEmitter();
    @Output() requestFormInstanceIds = new EventEmitter();
    @Output() onPropertyUpdated = new EventEmitter();
    @Output() loadFormInstanceElement = new EventEmitter();
    @Output() newObjectCreated = new EventEmitter();

    //TEAMS-561: KLW - New emitter to handle validation
    @Output() formStatusChanged = new EventEmitter();
    //TEAMS-838: KLW - New emitter to handle validation for the Kendo/Telerik Grids
    @Output() wrapperFormControlCreated = new EventEmitter();

    @Output() conditionalLogicInstructions = new EventEmitter(); // VNEXT-1383, in particular the third use case.

    @Output() mimickedNgModelChange = new EventEmitter();

    public FieldTypes: FormFieldTypeSelection[];

    private wrappedFormComponent: FormFieldBaseComponent = null;
    private footnoteComponent: FormFieldBaseComponent = null;

    private formFieldNames: string[] = null; // We will get this from the child component.
    private constraintNames: string[] = null;
    private customRegexConstraintName: string = null;
    private displayFormatValues: string[] = null;
    private displayRowsValues: string[] = null;
    //isFormControlValid: string;
    private wrappedFieldHasError: boolean = false;
    private displayFormFieldPropertiesEdit: boolean = true;

    // Define a reference to my dynamic component directive.
    // Note:  the following three definitions show multiple ways to get the dynamic component host directive,
    //        but the latter two are commented out as the first approach, like the other two, works fine/suffices.
    @ViewChild(DynamicComponentHostDirective, { read: DynamicComponentHostDirective }) dynamicComponentHost: DynamicComponentHostDirective;

    // Define a reference to a dynamically created (wrapped) form field component.
    private componentRef = null;
    // Define a form field wrapper operating mode.
    private wrapperOperatingMode: WrapperOperatingMode =
        WrapperOperatingMode.STATIC_COMPONENT;

    private bShowFormConditionalLogic: boolean = true;

    @ViewChild('validationErrorMessage') validationErrorMessageLi: ElementRef;
    //VNEXT-539: KLW - Used for allowing required for multi select and auto complete
    @ViewChild('errorDiv') errorDiv: ElementRef;

    // Constructor.
    public constructor(public dialog: MatDialog,
        private componentFactoryResolver: ComponentFactoryResolver,
        private fieldDefinitionService: FieldDefinitionService,
        protected formFieldTypeAndNameService: FormFieldTypeAndNameService,
        private renderer: Renderer2) { // Note:  we only use this to get field type display names.
        super();

        return;
    }

    // Implement abstract methods.
    public getProperties(): any {
        let hshProperties = null;

        if (this.wrappedFormComponent) {
            hshProperties = this.wrappedFormComponent.getProperties();
        }

        return (hshProperties);
    }

    public hasNumericData(): boolean {
        return (this.wrappedFormComponent ? this.wrappedFormComponent.hasNumericData() : false);
    }
    public getNumericValue(formFieldParam: FormField,
        formInstanceElementParam: FormInstanceElement,
        gridRow: IGridRow,
        processingPhase: FormFieldProcessingPhaseEnum): number {
        return (this.wrappedFormComponent ?
            this.wrappedFormComponent.getNumericValue(formFieldParam,
                formInstanceElementParam,
                gridRow,
                processingPhase) :
            0);
    }

    //TEAMS-838: KLW - When the form control of a form field has been created, emit that its form control is ready to be used
    public handleFormControlCreated(event: any) {
        this.wrapperFormControlCreated.emit(event);
    }

    public setFocus(): void {
        if (this.wrappedFormComponent) {
            this.wrappedFormComponent.setFocus();
        }

        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.FieldTypes = [];
        let ft = this.formFieldTypeAndNameService.getAllFieldTypesAndNameFields();
        ft.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
        for (let index: number = 0; index < ft.length; index++) {
            if (this.Mode !== 'gridDesign') { //skip grid-only field types
                if (ft[index].fieldDefinition.fieldDefinitionClassName == FormulaFieldType) continue;
                //if (ft[index].fieldDefinition.fieldDefinitionClassName == ValueConcatenatorFieldType) continue;
                if (ft[index].fieldDefinition.fieldDefinitionClassName == GridCascadingDropDownFieldType) continue;
                if (ft[index].fieldDefinition.fieldDefinitionClassName == GridFieldType) continue;
            }
            
            this.FieldTypes.push({
                fieldDefinitionClassName: ft[index].fieldDefinition.fieldDefinitionClassName,
                iconType: ft[index].iconType,
                iconName: ft[index].IconName,
                fieldName: ft[index].fieldName
            });
        }
        return;
    }

    public ngOnDestroy(): void {
        if (this.wrapperOperatingMode === WrapperOperatingMode.DYNAMIC_COMPONENT) {
            // We need to destroy our dynamically created component.

            // Note:  when we start using dynamically created components,
            //        we will probably need to release resources here.
        }

        return;
    }

    public ngAfterViewInit(): void {
        // Note:  @ViewChild() and @ViewChildren() value can be seen
        //        when this method is called but generally not before.

        // Load my form field dynamically.
        // Note:  the following call has been partially tested but is presently disabled
        //        as loading wrapped form fields dynamically is not presently a high priority.
        //this.loadFormFieldDynamically();
        if (this.wrapperOperatingMode === WrapperOperatingMode.DYNAMIC_COMPONENT) {
            setTimeout(() => {
                this.loadFormFieldDynamically();
            }, 10);
        }

        if (this.wrappedFormComponent == null) {
        }

        return;
    }

    public ngAfterContentInit(): void {
        return;
    }

    //VNEXT-811: KLW - Needed for emailing contacts
    public get FormInstanceName(): string {
        var retVal: string = '';

        if (this.formInstance)
            retVal = this.formInstance.name;

        return retVal;
    }

    public get GetFormFieldTypeIconName(): string {
        let field = this.formFieldTypeAndNameService.getFieldTypeAndField(this.FormField.fieldDefinitionClassName);
        if (field) return field.IconName;
        return 'stop';
    }
    public get GetFormFieldTypeIconType(): string {
        let field = this.formFieldTypeAndNameService.getFieldTypeAndField(this.FormField.fieldDefinitionClassName);
        if (field) return field.iconType;
        return 'MaterialIcon';
    }
    public get GetFieldTypeTitle(): string {
        return this.formFieldTypeAndNameService.getFieldNameFromFieldType(this.formField.fieldDefinitionClassName);
    }

    // Accessor methods.
    public get WrappedFieldHasError(): boolean {
        return this.WrappedFormComponent?.FieldHasValidationError;
    }

    public get WrappedFormComponent(): FormFieldBaseComponent {
        return (this.wrappedFormComponent);
    }

    //TEAMS-561: KLW - Logic for validation and enabling the save button
    public WrappedFieldCheckValidation() {
        this.WrappedFormComponent?.CheckFieldForValidation();
    }

    public get ValidationErrorMessage(): string {
        let errorMessage: string = '';

        let errorMessages: string[] = this.WrappedFormComponent?.getValidationErrors(this.revealValidationErrors);
        if ((errorMessages != null) && (errorMessages.length > 0))
            errorMessage = errorMessages[0];

        return errorMessage;
    }

    public get DisplayFormFieldPropertiesEdit(): boolean {
        return this.CanEditProperties && this.displayFormFieldPropertiesEdit;
    }

    //TEAMS-838: KLW - Return the form control when it's been created
    public get FormControl(): UntypedFormControl {
        return this.wrappedFormComponent != null ? this.wrappedFormComponent.FormControl : null;
    }

    public getCssClass(strElementName: string): string {
        let strCssClass: string = '';

        if (true) {
            switch (strElementName) {
                case 'outer-div':
                    strCssClass = 'flexbox-div';
                    break;
            }
        }

        return (strCssClass);
    }

    public get WrapperOperatingMode(): WrapperOperatingMode {
        return (this.wrapperOperatingMode);
    }

    public get ShowFormConditionalLogic(): boolean {
        let bShow: boolean = ((this.Mode !== 'gridDesign') && this.bShowFormConditionalLogic && (this.wrappedFormComponent != null) && this.wrappedFormComponent.canHaveFieldConditionalLogic);

        return (bShow);
    }

    public formFieldUpdated(): void {
        // 03-14-2024 note:  added this method so it can be calld by the field conditional
        //                   logic to indicate that a component's form field has been updated.

        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.formFieldUpdated();
    }
    public isCompoundObjectComponent(): boolean {
        return this.wrappedFormComponent != null ? this.wrappedFormComponent.isCompoundObjectComponent() : false;
    }
    public applyFilter(filterValue: string, filterOperator: string, childFieldName: string): void {
        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.applyFilter(filterValue, filterOperator, childFieldName);
    }
    public resetConditionalLogicSettings(): void {
        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.resetConditionalLogicSettings();
    }
    public applyChildFieldAttributes(childFieldName: string, showChildField: boolean, childFieldIsReadOnly: boolean, childFieldIsRequired: boolean): void {
        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.applyChildFieldAttributes(childFieldName, showChildField, childFieldIsReadOnly, childFieldIsRequired);
    }

    public get InstructionsAndFieldAdditionalStyling(): string {
        let additionalStyling: string = '';

        if (this.FormField != null) {
            let clientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(this.FormField.fieldDefinitionClassName);
            if (!clientLogic.canHaveInstructions)
                additionalStyling = 'margin-bottom: 0px;';
        }

        return additionalStyling;
    }
    public get ChildFormFieldLayoutAdditionalStyling(): string {
        let additionalStyling: string = '';

        if (this.FormField != null) {
            let clientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(this.FormField.fieldDefinitionClassName);
            if (clientLogic.hasCustomFormFieldLayoutStylesFor(this.FormField))
                additionalStyling = clientLogic.customFormFieldLayoutStylesFor(this.FormField);
        }

        return additionalStyling;
    }

    public get FieldMode(): string {
        //return (this.Mode);
        return (this.Mode !== 'gridDesign' ? this.Mode : 'design');
    }

    public get TooltipText(): string {
        let tooltipText: string = 'Click to edit field properties';

        let formField: FormField = (this.wrappedFormComponent != null ? this.wrappedFormComponent.FormField : null);
        if (formField != null) {
            let fieldName: string = ((formField.displayName != null) && (formField.displayName.trim() != '') ? formField.displayName : formField.name);

            tooltipText = `Click to edit properties for field '${fieldName}'`;
            // Note:  the following block fixes VNEXT-202, tooltips not showing.
            if (this.FormField?.toolTip && this.FormField.toolTip.trim() != '')
                tooltipText = this.FormField.toolTip;
        }

        return (tooltipText);
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'left';

        return (position);
    }

    // Outputs/emitters.
    public handleOnInitEvent(eventData: any): void {
        if (eventData) {
            // Save the property names being provided by my child component.
            this.wrappedFormComponent = <FormFieldBaseComponent>eventData['component'];

            // For grids, allow any column to be read only.
            this.formFieldNames = <string[]>eventData['properties'];

            //TEAMS-424: KLW - Add the default properties to the Form Field
            if ((this.formFieldNames == null) || (this.formFieldNames.indexOf(FormFieldPropertyEnum.NO_DEFAULT_PROPERTIES) < 0))
                this.formFieldNames = this.formFieldNames.concat(this.DefaultProperties);

            // 02-22-2021:  begin new code related to footnotes.
            if ((eventData[FormFieldOnInitPropertyEnum.SAVE_DATA_REQUIRED] != true) &&
                (this.FormField.hasFootnote == true)) {
                // Regardless of what type of form field this component is wrapping,
                // a call to saveData() is required if the wrapped form field has
                // a footnote.
                eventData[FormFieldOnInitPropertyEnum.SAVE_DATA_REQUIRED] = true;
            }
            // End 02-22-2021 footnote-related changes.

            if (eventData['constraintNames']) {
                this.constraintNames = <string[]>eventData['constraintNames'];

                if (eventData['customRegexConstraintName'])
                    this.customRegexConstraintName = <string>eventData['customRegexConstraintName'];
            }

            if (eventData['displayFormatValues']) {
                this.displayFormatValues = <string[]>eventData['displayFormatValues'];
            }

            if (eventData['displayRowsValues']) {
                this.displayRowsValues = <string[]>eventData['displayRowsValues'];
            }

            // 04-01-2020 change:  replace the component in the event data
            //                     with this wrapper component.  This will
            //                     require that this wrapper class passes on
            //                     calls to certain base class methods to
            //                     the wrapped component.
            eventData['component'] = this;

            if (eventData['displayPropertiesEdit'] != null) {
                this.displayFormFieldPropertiesEdit = eventData['displayPropertiesEdit'];
            }

            // Also pass this event on to my parent.
            this.onInit.emit(eventData);
        }
    }

    public handleFieldValidated(statusChangeName: string): void {
        if (this.validationErrorMessageLi != null) {
            let errorMessage: string = this.ValidationErrorMessage;

            // For reference only:
            //
            // Note:  the following two lines do work as lone as the following line is enabled above:
            //
            //             declare var $: any; // Declares jQuery
            //
            //        I am showing these two lines for reference even though it is more of an Angular
            //        practice to use Renderer2 instead of jQuery.
            //let jQuerySelector = $(this.validationErrorMessageLi.nativeElement);
            //jQuerySelector.text(errorMessage);

            this.renderer.setProperty(this.validationErrorMessageLi.nativeElement, 'innerText', errorMessage);

            //VNEXT-539: KLW - Used for allowing required for multi select and auto complete
            if (errorMessage != "") {
                this.errorDiv.nativeElement.setAttribute('style', 'display:block');
            }
            else {
                this.errorDiv.nativeElement.setAttribute('style', 'display:none');
            }

            // Note:  the following line could be used instead of the line just above.
            //this.renderer.setProperty(this.validationErrorMessageLi.nativeElement, 'innerHTML', errorMessage);

            //TEAMS-561: KLW - Emit the status change for validation
            this.formStatusChanged.emit(statusChangeName);
        }
    }

    public handleOnPropertyUpdated(event: any): void {
        this.onPropertyUpdated.emit(event);
    }

    public handleFootnoteOnInitEvent(eventData: any): void {
        // Is the form field that is emitting this event a footnote?
        if (eventData[FormFieldOnInitPropertyEnum.IS_FOOTNOTE]) {
            this.footnoteComponent = eventData['component'];
        }

        return;
    }

    public handleEditPropertiesClickEvent(): void {
        // Note:  the following line allows a component, e.g. a grid component,
        //        to know that it's own properties are being edited.
        if (this.wrappedFormComponent != null) {
            this.wrappedFormComponent.handlingEditPropertiesClickEvent();

            // Tell my parent that the properties for
            // the child form field need to be edited.
            let hshEventData = {
                //formField: this.formField,
                formField: this.wrappedFormComponent.FormField,
                properties: this.formFieldNames,
                constraintNames: this.constraintNames,
                customRegexConstraintName: this.customRegexConstraintName,
                displayFormatValues: this.displayFormatValues,
                displayRowsValues: this.displayRowsValues,

                component: this // Added on 04-01-2020.
            };

            this.onEdit.emit(hshEventData);
        }
    }

    public handleGridEditColumnPropertiesEvent(hshEventData: any): void {
        // Pass this column's edit event
        // to the parent form builder.
        hshEventData['component'] = this;
        this.onEdit.emit(hshEventData);

        return;
    }

    public fieldConditionalLogicClick(): void {
        // Tell my parent that the user wishes to edit
        // conditional logic triggered by this (wrapped) field.
        let hshEventData = {
            formField: this.FormField,

            component: this // Added on 04-01-2020.
        };

        this.onConditionalLogicEdit.emit(hshEventData);

        return;
    }

    // Note:  change the name of the following method to ngModelChange()
    //        to avoid confusion with the Angular ngOnChanges() method.
    public handleNgOnChange(): void {
        this.notifyValueChanged();

        let hashWithFormFieldAndFormInstanceElement = {
            formField: this.wrappedFormComponent.FormField,
            formInstanceElement: this.wrappedFormComponent.FormInstanceElement
        };
        this.mimickedNgModelChange.emit(hashWithFormFieldAndFormInstanceElement);
    }

    // Added for bug fix VNEXT-1590 in which handleNgOnChange() is no longer getting called for multi-selects
    // This provides a way for a field component to send its data for handling prior to a save (the event is handled
    // in GridFieldComponent where the event data is placed into a structure representing the data for an entire GridRow)
    // Maybe the event name should be changed to something like gridCellValueUpdated to it's clearer what it does
    public reEmitMimickedNgModelChange(eventData: any) {
        this.mimickedNgModelChange.emit(eventData);
    }

    public handleTouched(eventData: any): void {
        // Use for future event-driven validation checks to be preferred over the html view
        // calling the wrapped field's getValidationErrors() method as it does today
        this.touched.emit(eventData); // Pass this event to my parent.
    }

    public handleDisplayError(eventData: AlertDialogModel): void {
        // Display an error dialogue.
        this.dialog.open(AlertDialogComponent, {
            disableClose: true,
            maxWidth: "600px",
            data: eventData
        });
    }

    public handleLoadFormInstanceElement(loadRequest: any): void {
        // Pass this on to my parent component.
        this.loadFormInstanceElement.emit(loadRequest);
    }

    public handleConditionalLogicInstructions(instructionsForFormRenderer: TargetFieldConditionalLogicInstructions[]): void {
        this.conditionalLogicInstructions.emit(instructionsForFormRenderer);
    }

    public handleContextMenu(mouseEvent: MouseEvent): void {
        // Notify my parent.
        this.onContextMenuClick.emit(mouseEvent);

        return;
    }

    public handleRequestFormInstanceIds(eventData: any): void {
        // Let my parent handle this request.
        this.requestFormInstanceIds.emit(eventData);
        return;
    }

    public handleFieldDesignChange() {
        this.designChange.emit();
    }

    public handleNewObjectCreated(event: any) {
        this.newObjectCreated.emit(event);
    }

    // Implement base class methods that must be passed to the wrapped component.
    public saveConfiguration(form: any, reasonForSave: FormFieldConfigurationSaveReason): void {
        this.wrappedFormComponent.saveConfiguration(form, reasonForSave);
    }
    public saveConfigurationCompleted(form: any): void {
        this.wrappedFormComponent.saveConfigurationCompleted(form);
    }

    public saveData(formInstance: any): void {
        // Note:  even though we may have registered for this call
        //        only because the wrapped form field has a footnote,
        //        it will not hurt to call saveData() on the form
        //        field with the footnote, even if it does not need
        //        to save its data, as the saveData() implementation
        //        would be implemented as a NOOP.
        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.saveData(formInstance);

        if (this.footnoteComponent) {
            // If the wrapped form field has a footnote,
            // allow the footnote to package its data, if any.
            this.footnoteComponent.saveData(formInstance);
        }
    }
    //public saveCompleted(hshSavedComponentData: any): void {
    public saveCompleted(formInstance: any): void {
        this.wrappedFormComponent.saveCompleted(formInstance);
    }

    public propertyUpdated(formField: FormField, propertyName: string): void {
        this.wrappedFormComponent.propertyUpdated(formField, propertyName);
    }

    public receiveFormFieldNames(formFieldNames: string[],
        hshColNameToFormField: IFieldNameToFormField): void {
        this.wrappedFormComponent.receiveFormFieldNames(formFieldNames, hshColNameToFormField);
    }

    public requiresFieldValueUpdate(): boolean {
        return this.wrappedFormComponent != null ? this.wrappedFormComponent.requiresFieldValueUpdate() : false;
    }
    public formFieldValueUpdated(iColIndex: number, formField: FormField, formInstanceElement: FormInstanceElement, gridRow: IGridRow): boolean {
        let bComponentAffected: boolean =
            this.wrappedFormComponent.formFieldValueUpdated(iColIndex,
                formField,
                formInstanceElement,
                gridRow);

        return (bComponentAffected);
    }

    public formInstanceElementUpdated(formInstanceElement: FormInstanceElement): void {
        this.wrappedFormComponent.formInstanceElementUpdated(formInstanceElement);
    }

    public resetFormField(formField: FormField): void {
        this.wrappedFormComponent.resetFormField(formField);
    }

    // Implement formInstanceElementReceived().
    protected formInstanceElementReceived(): void {
        if ((this.Mode === 'preview') || (this.Mode === 'instance')) { }
    }

    public pseudoStatic_getDisplayValue(formFieldParam: FormField,
        formInstanceElementParam: FormInstanceElement,
        gridRow: IGridRow,
        processingPhase: FormFieldProcessingPhaseEnum): string {
        if (this.wrappedFormComponent) {
            return (this.wrappedFormComponent.pseudoStatic_getDisplayValue(formFieldParam,
                formInstanceElementParam,
                gridRow,
                processingPhase));
        } else {
            return (formInstanceElementParam.textValue);
        }
    }

    public get ShowFieldTypeSelector(): boolean {
        if (this.mode == 'instance') return false;
        if (this.mode == 'preview') return false;
        if (this.mode == 'gridDesign') return false;
        if (this.FormField.fieldDefinitionClassName == FormFieldTypeEnum.GridFieldType) return false;
        if (this.FormField.fieldDefinitionClassName == FormFieldTypeEnum.ContactsFieldType) return false;
        if (this.FormField.fieldDefinitionClassName == FormFieldTypeEnum.CommentsFieldType) return false;
        if (this.FormField.fieldDefinitionClassName == FormFieldTypeEnum.FormTemplateInstancesFieldDefinition) return false;
        return true;
    }

    // Override a method used to get my form field.
    public getFormField(): FormField {
        return (this.wrappedFormComponent ? this.wrappedFormComponent.FormField : null);
    }

    // Override a method used to get my class.
    public getFormFieldClass(): AngularCoreType<any> {
        return (this.wrappedFormComponent ? this.wrappedFormComponent.getFormFieldClass() : null);
    }

    public formInstanceElementLoaded(formInstanceElement: FormInstanceElement): void {
        if (this.wrappedFormComponent != null)
            this.wrappedFormComponent.formInstanceElementLoaded(formInstanceElement);
    }

    // Implement private helper methods.
    private loadFormFieldDynamically(): void {
        // Get the dynamic component init info that
        // corresponds to my form field definition.
        let componentInitInfo: DynamicComponentInitInfo =
        {
            fieldType: this.FormField.fieldDefinitionClassName,
            componentClass: mapFormFieldTypesToClasses[this.FormField.fieldDefinitionClassName]
        };

        if ((componentInitInfo !== undefined) && (componentInitInfo !== null)) {
            this.loadDynamicComponent(componentInitInfo);
        } else {
            console.error('FormFieldWrapper: loadFormFieldDynamically():  unable to create a form field component dynamically.');
        }

        return;
    }

    private loadDynamicComponent(componentInitInfo: DynamicComponentInitInfo): void {
        // Clear any existing content.
        const viewContainerRef = this.dynamicComponentHost.viewContainerRef;
        viewContainerRef.clear();

        // Get the component factory for the current dynamic component.
        const componentFactory =
            this.componentFactoryResolver.resolveComponentFactory(componentInitInfo.componentClass);

        // Create the dynamic component with the content host view.
        const componentRef = viewContainerRef.createComponent(componentFactory);
        // Save a reference to the dynamically created form field.
        this.componentRef = componentRef;

        // Initialize the component.
        // Note:  this code is TBD.
        //let dynamicComponent: IDynamicComponent = <IDynamicComponent>componentRef.instance;
        //dynamicComponent.data = componentInitInfo.data;
        this.componentRef.instance.formField = this.FormField;
        this.componentRef.instance.mode = this.mode;
        this.componentRef.instance.readOnly = this.readOnly;

        this.componentRef.instance.onInit.subscribe(eventData => {
            console.log("FormFieldWrapperComponent:  handling output event 'onInit()' ...");
        }
        )

        // If we have our form instance element, pass it
        // to the dynamically loaded form field component.
        if ((this.FormInstanceElement !== undefined) && (this.FormInstanceElement !== null)) {
            this.componentRef.instance.writeValue(this.FormInstanceElement);
        }

        // Done.
        return;
    }

}
